import { useState, useEffect, useCallback } from "react";
import { X } from "@phosphor-icons/react";

interface FullscreenImageViewerProps {
	isOpen: boolean;
	images: Array<{ src: string; alt: string }>;
	initialIndex: number;
	onClose: () => void;
}

const FullscreenImageViewer = ({
	isOpen,
	images,
	initialIndex,
	onClose
}: FullscreenImageViewerProps) => {
	const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const [touchStart, setTouchStart] = useState<number | null>(null);

	useEffect(() => {
		if (isOpen) setCurrentIndex(initialIndex);
	}, [isOpen, initialIndex]);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowLeft" && currentIndex > 0)
				setCurrentIndex((i) => i - 1);
			if (e.key === "ArrowRight" && currentIndex < images.length - 1)
				setCurrentIndex((i) => i + 1);
		},
		[currentIndex, images.length, onClose]
	);

	useEffect(() => {
		if (!isOpen) return;
		document.addEventListener("keydown", handleKeyDown);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, handleKeyDown]);

	if (!isOpen || images.length === 0) return null;

	const handleTouchStart = (e: React.TouchEvent) => {
		setTouchStart(e.touches[0].clientX);
	};

	const handleTouchEnd = (e: React.TouchEvent) => {
		if (touchStart === null) return;
		const diff = touchStart - e.changedTouches[0].clientX;
		if (Math.abs(diff) > 50) {
			if (diff > 0 && currentIndex < images.length - 1)
				setCurrentIndex((i) => i + 1);
			if (diff < 0 && currentIndex > 0) setCurrentIndex((i) => i - 1);
		}
		setTouchStart(null);
	};

	return (
		<div
			className="fixed inset-0 z-50 bg-black flex flex-col"
			onClick={onClose}
		>
			{/* Close button */}
			<div className="absolute top-4 right-4 z-10">
				<button
					type="button"
					onClick={onClose}
					className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
					aria-label="Close"
				>
					<X size={20} weight="bold" className="text-white" />
				</button>
			</div>

			{/* Image */}
			<div
				className="flex-1 flex items-center justify-center p-4"
				onClick={(e) => e.stopPropagation()}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
			>
				<img
					src={images[currentIndex]?.src}
					alt={images[currentIndex]?.alt}
					className="max-w-full max-h-full object-contain"
					draggable={false}
				/>
			</div>

			{/* Counter + dots */}
			{images.length > 1 && (
				<div
					className="pb-8 flex flex-col items-center gap-3"
					onClick={(e) => e.stopPropagation()}
				>
					<span
						className="text-white/70 text-sm"
						aria-label={`Image ${currentIndex + 1} of ${images.length}`}
					>
						{currentIndex + 1} / {images.length}
					</span>
					<div className="flex gap-2">
						{images.map((_, i) => (
							<button
								key={i}
								type="button"
								onClick={() => setCurrentIndex(i)}
								aria-label={`Go to image ${i + 1}`}
								className={[
									"w-2 h-2 rounded-full transition-colors",
									i === currentIndex ? "bg-white" : "bg-white/40"
								].join(" ")}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default FullscreenImageViewer;
